import React, { useState, useMemo} from 'react';
import debounce from 'lodash.debounce';
import { getCustomerDetails } from '@gjensidige/se-my-pages-utils';
import { fetchCallRequest, getUserAuthLevel } from '../api/api/call-request';
import { dispatchProperMessage } from '../api/useStatusCode';
import { logInfoMessage, preparePayload } from '../api/utils';
import { 
  appText, 
  customerSelectionOptions, 
  getQueueDetails, 
  daysWithLabelAndValue, 
  defaultWorkingHours 
} from '../constants/texts';
import { reducer } from '../state/reducer';
import { CallMeActionKind, DayType, TimeslotOption, WorkingHours } from '../types';
import UiCallMe from './ui-call-me/UiCallMe';
import { getSortedDaysOptions, getTimeslotOptions } from '../api/utils';
import { EnonicDataType } from '../types';

export type OpenHoursKey = 'open_hours_MANDAG' | 'open_hours_TISDAG' | 'open_hours_ONSDAG' | 'open_hours_TORSDAG' | 'open_hours_FREDAG';

export function MyPagesFeaturesCallMeLater({isScheduledCall, configs}: {
  isScheduledCall?: boolean, 
  configs?: EnonicDataType
}) {

  function formatPhoneNumber(number: string): string {
    let result;
    if(number.length === 10 && number.charAt(0) === '0'){
      result = "+46" + number.substring(1);
    } else {
      result = "+46" + number;
    }
  
    return result;
  }
  const todayDate = new Date(Date.now());

  //To be configured via Enonic
  //Holidays should be in the format '2024-08-22|2024-08-26'
  const defaultOpenHours = configs?.open_hours || defaultWorkingHours;
  const enonicConfigs = {
    holidays: configs?.holidays || '',
    open_hours: defaultOpenHours,
    open_hours_MANDAG: configs?.open_hours_MANDAG || defaultOpenHours,
    open_hours_TISDAG: configs?.open_hours_TISDAG || defaultOpenHours,
    open_hours_ONSDAG: configs?.open_hours_ONSDAG || defaultOpenHours,
    open_hours_TORSDAG: configs?.open_hours_TORSDAG || defaultOpenHours,
    open_hours_FREDAG: configs?.open_hours_FREDAG || defaultOpenHours,
    lunch_break: configs?.lunch_break || '12-14'
  }
  const sortedDaysOptions = getSortedDaysOptions(
    daysWithLabelAndValue,
    todayDate,
    enonicConfigs.holidays
  );

  const todayDay = Object.keys(daysWithLabelAndValue).find(
    (dayKey) =>
      daysWithLabelAndValue[dayKey as keyof typeof daysWithLabelAndValue]
        .value === todayDate.getDay(),
  ) || 'MANDAG' as keyof typeof daysWithLabelAndValue;
  //To be configured via Enonic

  const todayTimeslots: string = enonicConfigs[`open_hours_${todayDay}` as OpenHoursKey] || enonicConfigs.open_hours;
  const todayCloseTime = todayTimeslots
    .split('-')
    .map((n: string) => Number(n))[1];
  const moreThan3hoursLeft = todayCloseTime - todayDate.getHours() > 4;
  const [day, setDay] = useState(
    moreThan3hoursLeft
      ? sortedDaysOptions[0]
      : sortedDaysOptions[1]
  );
  const chosenDay = Object.keys(daysWithLabelAndValue).find(
    (dayKey) =>
      daysWithLabelAndValue[dayKey as keyof typeof daysWithLabelAndValue]
        .value === day.value,
  );
// To be configured via Enonic
  const timeslotRange = chosenDay ? enonicConfigs[`open_hours_${chosenDay}` as OpenHoursKey] : enonicConfigs.open_hours;
  const rangeParts = timeslotRange.split('-').map((n: string) => Number(n));
  const open = rangeParts[0];
  const close = rangeParts[1];
  const availableTimeslots = getTimeslotOptions(
    open,
    close,
    day.value,
    todayDate,
    enonicConfigs.lunch_break
  );
  const initialTimeslot = availableTimeslots.find(
    (timeslot) =>
      timeslot.value.getHours() === Math.round(todayDate.getHours() + 4),
  );

  const [timeslot, setTimeslot] = useState<TimeslotOption | undefined>(
    initialTimeslot || availableTimeslots[0],
  );

  const selectedTimeslot = useMemo(
    () =>
      timeslot ??
      availableTimeslots[0],
    [availableTimeslots, timeslot],
  );

  const [contactFormData, dispatch] = React.useReducer(reducer, {
    callMeActionStatus: 'initial',
    name: '',
    number: '',
    day: day,
    time: selectedTimeslot,
    formErrors: {
      nameInvalid: false,
      numValidity: 'VALID'
    },
    isCustomer: '',
    callMeActionStatusMessage: ''
  });



  React.useEffect(() => {
    getUserAuthLevel().then((authLevel) => {
      if (authLevel && authLevel.value === 3) {
        getCustomerDetails().then((value) => {
          if (value) {
            dispatch({
              payload: value.name,
              type: CallMeActionKind.ENTER_NAME
            });
            dispatch({
              payload:
                value.phone.find((number) => number.primaryPhone)
                  ?.phoneNumber || '',
              type: CallMeActionKind.ENTER_NUM
            });
          }
        });
      }
    });
  }, []);
  const onChangeName = (name: string) => {
    dispatch({
      payload: name,
      type: CallMeActionKind.ENTER_NAME
    });
  };

  const onChangeNum = (num: string) => {
    dispatch({
      payload: num,
      type: CallMeActionKind.ENTER_NUM
    });
    debouncedMyFunction(num);
  };

  const onChangeDay = (day: DayType) => {
    setDay(day);
    dispatch({
      payload: day,
      type: CallMeActionKind.CHOOSE_DAY
    });
  }

  const onChangeTime = (time: {label: string, value: Date} | string) => {
    dispatch({
      payload: time,
      type: CallMeActionKind.CHOOSE_TIME
    });
  }

  const debouncedMyFunction = React.useMemo(
    () =>
      debounce((num: string) => {
        dispatch({
          payload: num,
          type: CallMeActionKind.VALIDATE_ENTER_NUM
        });
      }, 750),
    [dispatch]
  );


  const onSelectCustomerRelationShip = (option: string) => {
    dispatch({
      type: CallMeActionKind.SELECT_CUSTOMER_RELATIONSHIP,
      payload: option
    });
  };
  const onFormSubmit = React.useCallback(() => {
    dispatch({
      payload: '',
      type: CallMeActionKind.SET_LOADING
    });
    const queDetails = getQueueDetails(configs, !!isScheduledCall);
    const callMePayload = preparePayload(contactFormData, queDetails, !!isScheduledCall);
    fetchCallRequest(callMePayload).then((value) => {
      dispatchProperMessage(value.code, dispatch);
    });
    logInfoMessage('call me request published');
  }, [contactFormData]);

  return (
    <UiCallMe
      appText={appText}
      contactFormData={contactFormData}
      events={{
        onFormSubmit,
        onChangeName,
        onChangeNum,
        onChangeDay,
        onChangeTime,
        onSelectCustomerRelationShip
      }}
      customerSelectionOptions={customerSelectionOptions}
      isScheduledCall={!!isScheduledCall}
      sortedDaysOptions={sortedDaysOptions}
      availableTimeslots={availableTimeslots}
    />
  );
}

export default MyPagesFeaturesCallMeLater;


